import React, {useEffect, useState} from "react";
import {LookDataType} from "./LookTypes";
import SwiperBuilder, {defaultBreakpoints} from "../crossSelling/SwiperBuilder";
import {useTranslation} from "react-i18next";
import LookItem from "./LookItem";
import {BreakpointType} from "../../molecules/SwiperTypes";
import {fetchTagRelatedLooks, ResponseError} from "./stlHelper";

/* eslint-disable no-underscore-dangle */

type TagsPropsType = {currentLook: LookDataType; setLook: React.Dispatch<React.SetStateAction<LookDataType | null>>};

// _Outdoor_ wird schon durch genug andere "Räume" wie "Garten" oder "Terrasse" abgedeckt
// _Minimalistisch_ ist zu minimalistisch
const tagsToFilterOut = ["room:Outdoor", "designStyle:Minimalistisch"];

// Vom Kunden ausgewählte Filter Tags von der Übersichtsseite /LLP holen (LLP-Filter-Tag-Kombi)
const userSelectedTagList = document.referrer ? new URL(document.referrer).searchParams.get("taglist") : null;

const lookSliderBreakpoints: BreakpointType = {...defaultBreakpoints};
if (lookSliderBreakpoints["0"]) {
  lookSliderBreakpoints["0"] = {...lookSliderBreakpoints["0"], slidesPerView: 1.5};
}

const TagRelatedLooksSliders: React.FC<TagsPropsType> = ({currentLook, setLook}) => {
  const [sliderTags, setSliderTags] = useState<string[]>([]);

  useEffect(() => {
    const tagsObj: {[key: string]: string[]} = {};
    // Bestimmte Tags aussortieren (=> Wenn nur ein einzelner LLP-Filter-Tag vom Kunden ausgewählt wurde, soll zum gleichen Tag kein generischer Tag-Slider erstellt werden)
    const filteredTags = currentLook.tags.filter((tg) => !(tagsToFilterOut.includes(tg) || tg === userSelectedTagList));

    for (const filteredTag of filteredTags) {
      const tagKey = filteredTag.split(":")[0];

      // Namespace (der Teil vor dem Doppelpunkt) als key, und unter diesem die tags als string array speichern
      tagsObj[tagKey] = [...(tagsObj[tagKey] || []), filteredTag];

      const objValues = Object.values(tagsObj);
      if (objValues.length === 2 && objValues[1].length === 2) break;
    }

    // Nur die beiden ersten namespace keys nehmen
    const [first, second] = Object.keys(tagsObj).slice(0, 2);

    // Von denen jeweils nur die ersten beiden tags nehmen (maximal 4)
    const tagsArray = [...tagsObj[first].slice(0, 2), ...tagsObj[second].slice(0, 2)];

    // LLP-Filter-Tag-Kombi hinzufügen, falls vorhanden
    if (userSelectedTagList) tagsArray.unshift(userSelectedTagList);

    setSliderTags(tagsArray);
  }, [currentLook]);

  if (!sliderTags.length) {
    return null;
  }

  return (
    <React.Fragment>
      {sliderTags.map((tagString, index) => (
        <TagRelatedLooks
          key={index}
          index={index}
          setLook={setLook}
          tagString={tagString}
          currentLookId={currentLook._id}
        />
      ))}
    </React.Fragment>
  );
};

const TagRelatedLooks: React.FC<{
  index: number;
  tagString: string;
  currentLookId: string;
  setLook: React.Dispatch<React.SetStateAction<LookDataType | null>>;
}> = ({currentLookId, setLook, tagString, index}) => {
  const [looks, setLooks] = useState<LookDataType[] | null>(null);
  const [headlineTag, setHeadlineTag] = useState<string>("");
  const {t} = useTranslation();

  useEffect(() => {
    const usp = new URLSearchParams();
    usp.set("taglist", tagString);

    void fetchTagRelatedLooks(tagString)
      .then((result) => {
        const looksWithoutCurrent = result.filter((look) => look._id !== currentLookId);
        if (looksWithoutCurrent.length >= 4) {
          setLooks(looksWithoutCurrent);

          // Tagnamen für die Slider Überschrift herausfiltern
          // LLP-Filter-Tag-Kombi dafür passend formatieren
          if (index === 0 && userSelectedTagList) {
            const headlineTagList: string[] = [];
            const separatedTagStrings: string[] = userSelectedTagList.split(",");

            separatedTagStrings.forEach((tg) => {
              headlineTagList.push("#" + tg.split(":")[1]);
            });

            setHeadlineTag(headlineTagList.join(" "));
          } else {
            setHeadlineTag(tagString.split(":")[1]);
          }
        }
      })
      .catch((error: ResponseError) => {
        // eslint-disable-next-line no-console
        if (__DEVELOPMENT__) console.error("DEVELOP:", JSON.stringify(error.data));
      });
  }, [currentLookId]);

  if (!looks) {
    return null;
  }

  return (
    <div className="pos-rel">
      <SwiperBuilder
        title={t("shopthelook.furtherLooksTo--tag", {tag: headlineTag}).toUpperCase()}
        breakpoints={lookSliderBreakpoints}
      >
        {looks.map((look, key) => {
          const {origin, pathname, search} = window.location;

          const usp = new URLSearchParams(search);
          usp.set("look-id", look._id);
          const href = origin + pathname + "?" + usp.toString();

          const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            window.scrollTo({top: 0, behavior: "instant"});
            window.history.pushState(window.history.state, "clicked related look", href);
            setLook(look);
          };

          return <LookItem key={key} look={look} href={href} onClick={onClick} />;
        })}
      </SwiperBuilder>
    </div>
  );
};

export default TagRelatedLooksSliders;
